import type { FC, PropsWithChildren } from "react";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useModal, type ModalProps } from "@edreamsodigeo/prisma-design-system";

import useOpenByDevice from "~/clientState/hooks/useOpenByDevice";

export interface PrimeModalProviderProps {
  initialState: boolean;
}

interface GlobalContent {
  isOpen: boolean;
  setOpen: (param: boolean) => void;
}

export interface usePrimeBannerUIReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  modalProps: ModalProps;
}

export const PrimeSubscriptionModalContext = createContext<GlobalContent>({
  isOpen: false,

  setOpen: (param: boolean) => undefined,
});

const PrimeSubscriptionModalUIProvider: FC<
  PropsWithChildren<PrimeModalProviderProps>
> = ({ children, initialState }) => {
  const [isOpen, setOpen] = useOpenByDevice(initialState);

  const value = useMemo(() => ({ isOpen, setOpen }), [isOpen, setOpen]);

  return (
    <PrimeSubscriptionModalContext.Provider value={value}>
      {children}
    </PrimeSubscriptionModalContext.Provider>
  );
};

export function usePrimeSubscriptionModalUI(): usePrimeBannerUIReturn {
  const PrimeContext = useContext(PrimeSubscriptionModalContext);
  const isOpen = PrimeContext.isOpen;
  const setOpen = PrimeContext.setOpen;

  const {
    open: openModal,
    close: closeModal,
    modalProps,
  } = useModal({ escapeKeyEnabled: true });

  useEffect(() => {
    if (isOpen) {
      openModal();
    } else {
      closeModal();
    }
  }, [isOpen, closeModal, openModal]);

  const open = (): void => {
    setOpen(true);
  };

  const close = (): void => {
    setOpen(false);
  };

  return {
    isOpen,
    open,
    close,
    modalProps,
  };
}

export default PrimeSubscriptionModalUIProvider;
